import { NavLink, useLocation } from "react-router-dom";
import s from "./Layout.module.css";
import {
  IoBookOutline,
  IoBarChartOutline,
  IoStatsChartOutline,
  IoPeopleOutline,
  IoStarOutline,
  IoFileTrayFullOutline,
} from "react-icons/io5";
import { IoChatbubbleOutline } from "react-icons/io5";
import { LogoVertical } from "@datapay-by-datacom/design-system";
import { IoHardwareChipOutline } from "react-icons/io5";
import { PAY_COMPONENT_IMPORT_BASE_PATH, PAY_COMPONENT_IMPORT_DISPLAY_NAME } from "../../pay-component-import/config";
import { usePayComponentImportEnabled } from "../../pay-component-import/hooks/usePayComponentImportEnabled";
import {
  CLIENT_PAY_NORMALISATION_BASE_PATH,
  CLIENT_PAY_NORMALISATION_DISPLAY_NAME,
  useClientPayNormalisationEnabled,
} from "../../client-pay-normalisation";
import {
  SPLIT_COSTING_IMPORT_BASE_PATH,
  useSplitCostingImportEnabled,
} from "../../split-costing-import";
import {
  COMPONENT_MATCHING_IMPORT_BASE_PATH,
  COMPONENT_MATCHING_IMPORT_DISPLAY_NAME,
  useComponentMatchingImportEnabled,
} from "../../component-matching-import";
import { useCompanyConfigEnabled } from "../../onboarding/hooks/useCompanyConfigEnabled";
import { useWorkPatternImportEnabled } from "../../work-patterns/hooks/useWorkPatternImportEnabled";
import { getBrandName, getLogoUrl } from "../../brand-config";
import { useAuthenticated, useIsProvisioner } from "../../authn";
import { useEvalStewardEnabled } from "../../eval-steward";
import { useFeatureFlagValue, FeatureFlag } from "../../feature-flags";
import { useAppVersion } from "../hooks/useAppVersion";
import { useApiVersion } from "../hooks/useApiVersion";

function isRouteFamilyActive(pathname: string, basePath: string): boolean {
  const normalizedPathname = pathname.replace(/\/+$/, "").toLowerCase();
  const normalizedBasePath = basePath.replace(/\/+$/, "").toLowerCase();
  return (
    normalizedPathname === normalizedBasePath ||
    normalizedPathname.startsWith(`${normalizedBasePath}/`)
  );
}

export function SidebarMenu() {
  const { pathname } = useLocation();
  const auth = useAuthenticated();
  const shadowRunsEnabled = useFeatureFlagValue(FeatureFlag.EnableShadowRuns);
  const platformAnalyticsEnabled = useFeatureFlagValue(FeatureFlag.EnablePlatformAnalytics);
  const userManagementEnabled = useFeatureFlagValue(FeatureFlag.EnableUserManagement);
  const onboardingEnabled = useFeatureFlagValue(FeatureFlag.EnableOnboarding);
  const agenticPlatformEnabled = useFeatureFlagValue(FeatureFlag.EnableAgenticPlatform);
  const { isEvalStewardEnabled } = useEvalStewardEnabled();
  const { isEnabled: companyConfigEnabled } = useCompanyConfigEnabled();
  const { isEnabled: payComponentImportEnabled } = usePayComponentImportEnabled();
  const { isEnabled: workPatternImportEnabled } = useWorkPatternImportEnabled();
  const { isEnabled: splitCostingImportEnabled } = useSplitCostingImportEnabled();
  const { isEnabled: clientPayNormalisationEnabled } =
    useClientPayNormalisationEnabled();
  const isSplitCostingImportRoute = isRouteFamilyActive(
    pathname,
    SPLIT_COSTING_IMPORT_BASE_PATH
  );
  const { isEnabled: componentMatchingImportEnabled } =
    useComponentMatchingImportEnabled();
  const roles =
    auth.state === "authenticated" ? auth.roles : [];
  const isProvisioner = useIsProvisioner();
  const showShadowRuns = shadowRunsEnabled && isProvisioner;
  const showPlatformAnalytics = platformAnalyticsEnabled && isProvisioner;
  const onboardingToolPaths = [
    "/onboarding/bulk-create-company",
    "/onboarding/bulk-create-redesign",
    PAY_COMPONENT_IMPORT_BASE_PATH,
    "/onboarding/work-pattern-import",
    "/onboarding/split-costing-import",
    CLIENT_PAY_NORMALISATION_BASE_PATH,
    COMPONENT_MATCHING_IMPORT_BASE_PATH,
  ];
  const isCompanyConfigRoute =
    pathname === "/onboarding" ||
    (pathname.startsWith("/onboarding/") &&
      !onboardingToolPaths.some(
        (toolPath) =>
          pathname === toolPath || pathname.startsWith(`${toolPath}/`)
      ));
  const showUserManagement =
    userManagementEnabled &&
    (isProvisioner ||
      roles.includes("user_admin") ||
      roles.includes("users_readonly"));

  return (
    <div className={s.sidebarMenu}>
      <NavLink to="/" end className={({ isActive }) => `${s.sidebarLink} ${isActive ? s.sidebarLinkActive : ""}`}>
        <IoChatbubbleOutline /> New Task
      </NavLink>
      <NavLink to="/org" className={({ isActive }) => `${s.sidebarLink} ${isActive ? s.sidebarLinkActive : ""}`}>
        <IoBookOutline /> Knowledge Bases
      </NavLink>
      {onboardingEnabled && (
        <>
          <div className={s.sidebarLink}>
            <IoFileTrayFullOutline /> Onboarding
          </div>
          {companyConfigEnabled && (
            <>
              <NavLink
                to="/onboarding"
                end
                aria-current={isCompanyConfigRoute ? "page" : undefined}
                className={() => `${s.sidebarLink} ${s.sidebarSubLink} ${isCompanyConfigRoute ? s.sidebarLinkActive : ""}`}
              >
                Company Configurations
              </NavLink>
              <NavLink to="/onboarding/bulk-create-company" className={({ isActive }) => `${s.sidebarLink} ${s.sidebarSubLink} ${isActive ? s.sidebarLinkActive : ""}`}>
                Bulk create companies
              </NavLink>
            </>
          )}
          {payComponentImportEnabled && (
            <NavLink to={PAY_COMPONENT_IMPORT_BASE_PATH} className={({ isActive }) => `${s.sidebarLink} ${s.sidebarSubLink} ${isActive ? s.sidebarLinkActive : ""}`}>
              {PAY_COMPONENT_IMPORT_DISPLAY_NAME}
            </NavLink>
          )}
          {workPatternImportEnabled && (
            <NavLink to="/onboarding/work-pattern-import" className={({ isActive }) => `${s.sidebarLink} ${s.sidebarSubLink} ${isActive ? s.sidebarLinkActive : ""}`}>
              Work Pattern Import
            </NavLink>
          )}
          {splitCostingImportEnabled && (
            <NavLink
              to={SPLIT_COSTING_IMPORT_BASE_PATH}
              aria-current={isSplitCostingImportRoute ? "page" : undefined}
              className={() =>
                `${s.sidebarLink} ${s.sidebarSubLink} ${
                  isSplitCostingImportRoute ? s.sidebarLinkActive : ""
                }`
              }
            >
              Split Costing Import
            </NavLink>
          )}
          {clientPayNormalisationEnabled && (
            <NavLink
              to={CLIENT_PAY_NORMALISATION_BASE_PATH}
              className={({ isActive }) =>
                `${s.sidebarLink} ${s.sidebarSubLink} ${isActive ? s.sidebarLinkActive : ""}`
              }
            >
              {CLIENT_PAY_NORMALISATION_DISPLAY_NAME}
            </NavLink>
          )}
          {componentMatchingImportEnabled && (
            <NavLink
              to={COMPONENT_MATCHING_IMPORT_BASE_PATH}
              className={({ isActive }) =>
                `${s.sidebarLink} ${s.sidebarSubLink} ${isActive ? s.sidebarLinkActive : ""}`
              }
            >
              {COMPONENT_MATCHING_IMPORT_DISPLAY_NAME}
            </NavLink>
          )}
        </>
      )}
      {agenticPlatformEnabled && isProvisioner && (
        <NavLink to="/agentic-platform" className={({ isActive }) => `${s.sidebarLink} ${isActive ? s.sidebarLinkActive : ""}`}>
          <IoHardwareChipOutline /> Agentic Platform
        </NavLink>
      )}
      {showShadowRuns && (
        <NavLink to="/shadow-runs" className={({ isActive }) => `${s.sidebarLink} ${isActive ? s.sidebarLinkActive : ""}`}>
          <IoBarChartOutline /> Shadow Runs
        </NavLink>
      )}
      {showPlatformAnalytics && (
        <NavLink to="/platform-analytics" className={({ isActive }) => `${s.sidebarLink} ${isActive ? s.sidebarLinkActive : ""}`}>
          <IoStatsChartOutline /> Platform Analytics
        </NavLink>
      )}
      {isEvalStewardEnabled && (
        <NavLink to="/eval-steward" className={({ isActive }) => `${s.sidebarLink} ${isActive ? s.sidebarLinkActive : ""}`}>
          <IoStarOutline /> Eval Steward
        </NavLink>
      )}
      {showUserManagement && (
        <NavLink to="/user-management" className={({ isActive }) => `${s.sidebarLink} ${isActive ? s.sidebarLinkActive : ""}`}>
          <IoPeopleOutline /> User Management
        </NavLink>
      )}
    </div>
  );
}

export function SidebarVersionInfo() {
  const appVersion = useAppVersion();
  const apiVersion = useApiVersion();

  const webLabel = appVersion ? `Web v${appVersion}` : "Web ...";
  const apiLabel = apiVersion ? `API v${apiVersion}` : "API ...";

  return (
    <div className={s.versionInfo}>
      {webLabel} &middot; {apiLabel}
    </div>
  );
}

export function SidebarFooter() {
  const logoUrl = getLogoUrl();
  return (
    <div className={`${s.sidebarMenu} ${s.sidebarFooter}`}>
      <div className={s.logoFooterContainer}>
        <div>
          {logoUrl ? (
            <img src={logoUrl} alt={getBrandName()} className={s.smallLogo} />
          ) : (
            <LogoVertical />
          )}
        </div>
        <div>
          <span className={s.footerTitle}>Payroll Assistant</span>
          <span className={s.footerSubtitle}>Powered by {getBrandName()}</span>
        </div>
      </div>
    </div>
  );
}
